import { Link } from 'react-router-dom'
import type { TicketChecklistItem, TicketEvent } from '../../lib/api'

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
}

const PRIORITY_LABELS: Record<string, string> = {
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
}

function articleTitle(event: TicketEvent): string | null {
  const title = event.article?.title
  return typeof title === 'string' && title.length > 0 ? title : null
}

function knownLabel(value: unknown, labels: Record<string, string>): string | null {
  return typeof value === 'string' && value in labels ? labels[value] : null
}

function personName(value: unknown, names: Record<string, string>): string {
  if (typeof value !== 'string' || value.length === 0) return 'a teammate'
  return names[value] ?? 'a teammate'
}

function formatWhen(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) return 'none'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'none'
  return date.toLocaleString()
}

export function ticketEventSummary(
  event: TicketEvent,
  names: Record<string, string>,
  checklistText: Record<string, string>,
): string {
  const previous = event.old_values ?? {}
  const next = event.new_values ?? {}

  switch (event.event_type) {
    case 'created':
      return 'Ticket created'
    case 'status_changed': {
      const from = knownLabel(previous.status, STATUS_LABELS)
      const to = knownLabel(next.status, STATUS_LABELS)
      return from && to ? `Status changed from ${from} to ${to}` : 'Status changed'
    }
    case 'priority_changed': {
      const from = knownLabel(previous.priority, PRIORITY_LABELS)
      const to = knownLabel(next.priority, PRIORITY_LABELS)
      return from && to ? `Priority changed from ${from} to ${to}` : 'Priority changed'
    }
    case 'assignee_added':
      return `Added ${personName(next.user_id, names)} as an assignee`
    case 'assignee_removed':
      return `Removed ${personName(previous.user_id, names)} as an assignee`
    case 'comment_added': {
      if (next.staff === true) return 'Staff comment added'
      if (next.source === 'system') return 'System note recorded'
      if (next.source === 'merchant_email') return 'Merchant reply recorded'
      return 'Comment added'
    }
    case 'checklist_completed': {
      const itemId = next.checklist_item_id
      const text = typeof itemId === 'string' ? checklistText[itemId] : undefined
      return text ? `Checklist item completed: ${text}` : 'Checklist item completed'
    }
    case 'sla_due_changed':
      return `SLA due date changed from ${formatWhen(previous.sla_due_at)} to ${formatWhen(next.sla_due_at)}`
    case 'reopened': {
      const from = knownLabel(previous.status, STATUS_LABELS)
      const to = knownLabel(next.status, STATUS_LABELS)
      return from && to ? `Reopened from ${from} to ${to}` : 'Ticket reopened'
    }
    case 'article_linked': {
      const title = articleTitle(event)
      if (next.solved === true) {
        return title ? `Linked ${title} as the article that solved it` : 'Linked a Help article as the one that solved it'
      }
      return title ? `Linked ${title} as a related article` : 'Linked a related Help article'
    }
    case 'article_unlinked': {
      const title = articleTitle(event)
      return title ? `Unlinked ${title}` : 'Unlinked a Help article'
    }
    case 'article_solved_changed': {
      const title = articleTitle(event)
      if (next.solved === true) {
        return title ? `Marked ${title} as the article that solved it` : 'Marked a Help article as the one that solved it'
      }
      return title ? `Marked ${title} as a related article` : 'Marked a Help article as related'
    }
    default:
      return 'Ticket updated'
  }
}

function formatAge(date: string): string {
  const diffMinutes = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60000))
  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m`
  const hours = Math.floor(diffMinutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

export function TicketActivityTimeline({
  events,
  names,
  checklistItems,
}: {
  events: TicketEvent[]
  names: Record<string, string>
  checklistItems: TicketChecklistItem[]
}) {
  if (events.length === 0) {
    return <p className="text-sm text-gray-500">No activity yet.</p>
  }

  const checklistText = Object.fromEntries(checklistItems.map((item) => [item.id, item.text]))

  return (
    <ol aria-label="Ticket activity" className="space-y-4">
      {events.map((event) => {
        const actorName = event.actor?.name ?? 'System'
        const role = event.actor?.role?.replaceAll('_', ' ')
        return (
          <li key={event.id} className="flex gap-3">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${event.actor ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
              {event.actor ? (actorName[0] ?? '?') : 'S'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-800">{ticketEventSummary(event, names, checklistText)}</p>
              {event.article?.slug ? (
                <Link to={`/help/${event.article.slug}`} className="mt-1 inline-block text-sm font-medium text-blue-700 hover:text-blue-900">
                  Open article
                </Link>
              ) : null}
              <p className="mt-1 text-xs text-gray-500">
                <span className="font-semibold text-gray-700">{actorName}</span>
                {role ? <span className="capitalize"> · {role}</span> : null}
                <span title={new Date(event.created_at).toLocaleString()}> · {formatAge(event.created_at)} ago</span>
              </p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
